import React, { useState, useEffect, useCallback } from 'react';
import resourceService, { Resource } from '../../services/resourceService';
import segmentService from '../../services/segmentService';
import ResourceUploader from './ResourceUploader';
import ResourceList from './ResourceList';
import ResourceStats from './ResourceStats';

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
}

const ResourceManager: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSegment, setSelectedSegment] = useState<string>('all');
  const [showUploader, setShowUploader] = useState(false);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'autism', label: 'Autism Support' },
    { value: 'adhd', label: 'ADHD Management' },
    { value: 'learning-differences', label: 'Learning Differences' },
    { value: 'speech-language', label: 'Speech & Language' },
    { value: 'behavioral-support', label: 'Behavioral Support' },
    { value: 'general', label: 'General Resources' }
  ];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load resources and segments in parallel
      const [resourcesData, segmentsData] = await Promise.all([
        resourceService.getResources(
          selectedCategory === 'all' ? undefined : selectedCategory,
          selectedSegment === 'all' ? undefined : selectedSegment
        ),
        segmentService.getSegments()
      ]);

      setResources(resourcesData);
      setSegments(segmentsData);
    } catch (err) {
      console.error('Error loading resource data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedSegment]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResourceCreated = async (newResource: any) => {
    console.log('✅ Resource created successfully:', newResource);
    setShowUploader(false);
    await loadData(); // Refresh the list
  };

  const handleResourceUpdated = async () => {
    await loadData(); // Refresh the list
  };

  const handleResourceDeleted = async (resourceId: number) => {
    try {
      await resourceService.deleteResource(resourceId);
      await loadData(); // Refresh the list
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete resource');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resource Management</h1>
          <p className="text-gray-600">Manage lead magnets and downloadable resources</p>
        </div>
        <button
          onClick={() => setShowUploader(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <span>➕</span>
          Add Resource
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Resource Stats Overview */}
      <ResourceStats resources={resources} />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Segment
            </label>
            <select
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Segments</option>
              {segments.map(segment => (
                <option key={segment.id} value={segment.name}>
                  {segment.name} ({segment.active_leads} leads)
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={loadData}
              className="bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm hover:bg-gray-200"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Resource List */}
      <ResourceList
        resources={resources}
        segments={segments}
        onResourceUpdated={handleResourceUpdated}
        onResourceDeleted={handleResourceDeleted}
      />

      {/* Resource Uploader Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Add New Resource</h2>
                <button
                  onClick={() => setShowUploader(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>
              <ResourceUploader
                segments={segments}
                onSuccess={handleResourceCreated}
                onCancel={() => setShowUploader(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {resources.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
          <p className="text-gray-600 mb-4">
            {selectedCategory !== 'all' || selectedSegment !== 'all'
              ? 'Try adjusting your filters or create a new resource.'
              : 'Get started by uploading your first lead magnet or resource.'}
          </p>
          <button
            onClick={() => setShowUploader(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Add First Resource
          </button>
        </div>
      )}
    </div>
  );
};

export default ResourceManager;